import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  X,
  User,
  Palette,
  Sun,
  Moon,
  Monitor,
  Check,
  Link2,
  RefreshCw,
  Calendar,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Bell,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import { generateA11yId } from '../../utils/accessibility';
import { useGoogleCalendar } from '@/hooks/useGoogleCalendar';
import { useOrganization } from '@/context/OrganizationContext';
import { getDigestPreference, upsertDigestPreference } from '@/db/digestPreferences';
import { supabase } from '@/lib/supabaseClient';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ThemePalette = 'violet' | 'blue' | 'emerald' | 'rose' | 'amber' | 'cyan' | 'orange';

export type UserSettingsTab = 'user' | 'appearance' | 'integrations' | 'notifications';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  theme: ThemeMode;
  palette: ThemePalette;
  onThemeChange: (theme: ThemeMode) => void;
  onPaletteChange: (palette: ThemePalette) => void;
  initialTab?: UserSettingsTab;
}

const THEME_PALETTES: { id: ThemePalette; name: string; color: string; gradient: string }[] = [
  { id: 'violet', name: 'Viola', color: '#8b5cf6', gradient: 'from-violet-500 to-purple-600' },
  { id: 'blue', name: 'Blu', color: '#3b82f6', gradient: 'from-blue-500 to-indigo-600' },
  { id: 'emerald', name: 'Verde', color: '#10b981', gradient: 'from-emerald-500 to-teal-600' },
  { id: 'rose', name: 'Rosa', color: '#f43f5e', gradient: 'from-rose-500 to-pink-600' },
  { id: 'amber', name: 'Ambra', color: '#f59e0b', gradient: 'from-amber-500 to-orange-600' },
  { id: 'cyan', name: 'Ciano', color: '#06b6d4', gradient: 'from-cyan-500 to-blue-600' },
  { id: 'orange', name: 'Arancio', color: '#f97316', gradient: 'from-orange-500 to-red-600' },
];

export function SettingsModal({
  isOpen,
  onClose,
  theme,
  palette,
  onThemeChange,
  onPaletteChange,
  initialTab = 'appearance',
}: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState<UserSettingsTab>(initialTab);

  // Generate stable IDs for ARIA attributes
  const modalLabelId = useMemo(() => generateA11yId('settings-modal-label'), []);
  const modalDescId = useMemo(() => generateA11yId('settings-modal-desc'), []);

  // Update active tab when initialTab changes
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const tabs = [
    { id: 'appearance' as UserSettingsTab, label: 'Aspetto', icon: Palette },
    { id: 'user' as UserSettingsTab, label: 'Profilo', icon: User },
    { id: 'integrations' as UserSettingsTab, label: 'Integrazioni', icon: Link2 },
    { id: 'notifications' as UserSettingsTab, label: 'Notifiche', icon: Bell },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalLabelId}
        aria-describedby={modalDescId}
        className="relative w-full max-w-3xl max-h-[85vh] bg-surface-secondary border border-border-primary rounded-xl shadow-2xl animate-scale-in flex overflow-hidden"
      >
        {/* Sidebar */}
        <div className="w-52 bg-surface-tertiary/50 border-r border-border-primary p-3 flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 mb-4">
            <User className="w-5 h-5 text-text-tertiary" />
            <span className="text-[14px] font-semibold text-text-primary">Il mio account</span>
          </div>

          <nav className="space-y-1" role="tablist" aria-label="Schede impostazioni">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                role="tab"
                aria-selected={activeTab === id}
                aria-controls={`settings-panel-${id}`}
                className={clsx(
                  'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-all',
                  activeTab === id
                    ? 'bg-accent-violet/10 text-accent-violet'
                    : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                )}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </nav>

          <div className="mt-auto pt-4 border-t border-border-primary">
            <div className="px-3 py-2">
              <p className="text-[10px] text-text-quaternary uppercase tracking-wider font-medium">
                Versione
              </p>
              <p className="text-[11px] text-text-tertiary mt-0.5">Zenda v1.0.0</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border-primary">
            <h2 id={modalLabelId} className="text-[15px] font-semibold text-text-primary">
              {tabs.find((t) => t.id === activeTab)?.label}
            </h2>
            <button
              onClick={onClose}
              aria-label="Chiudi impostazioni"
              className="p-1.5 rounded-md hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-tertiary"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Hidden description for screen readers */}
          <div id={modalDescId} className="sr-only">
            Impostazioni per la personalizzazione dell'aspetto e del profilo utente
          </div>

          {/* Content Area */}
          <div
            className="flex-1 overflow-y-auto p-6"
            role="tabpanel"
            id={`settings-panel-${activeTab}`}
            aria-labelledby={modalLabelId}
          >
            {activeTab === 'appearance' && (
              <AppearanceSettings
                theme={theme}
                palette={palette}
                onThemeChange={onThemeChange}
                onPaletteChange={onPaletteChange}
              />
            )}
            {activeTab === 'user' && <UserSettings />}
            {activeTab === 'integrations' && <IntegrationsSettings />}
            {activeTab === 'notifications' && <NotificationsSettings />}
          </div>
        </div>
      </div>
    </div>
  );
}

interface AppearanceSettingsProps {
  theme: ThemeMode;
  palette: ThemePalette;
  onThemeChange: (theme: ThemeMode) => void;
  onPaletteChange: (palette: ThemePalette) => void;
}

function AppearanceSettings({
  theme,
  palette,
  onThemeChange,
  onPaletteChange,
}: AppearanceSettingsProps) {
  const themeOptions = [
    { id: 'light' as ThemeMode, label: 'Chiaro', icon: Sun, description: 'Interfaccia luminosa' },
    { id: 'dark' as ThemeMode, label: 'Scuro', icon: Moon, description: 'Interfaccia scura' },
    { id: 'system' as ThemeMode, label: 'Sistema', icon: Monitor, description: 'Segue il sistema' },
  ];

  return (
    <div className="space-y-8">
      {/* Theme Mode */}
      <div>
        <h3 className="text-[13px] font-semibold text-text-primary mb-1">Tema</h3>
        <p className="text-[11px] text-text-tertiary mb-4">Scegli la modalità di visualizzazione</p>

        <div className="grid grid-cols-3 gap-3">
          {themeOptions.map(({ id, label, icon: Icon, description }) => (
            <button
              key={id}
              onClick={() => onThemeChange(id)}
              className={clsx(
                'relative flex flex-col items-center gap-2 p-4 rounded-xl border transition-all',
                theme === id
                  ? 'bg-accent-violet/10 border-accent-violet'
                  : 'bg-surface-tertiary/50 border-border-primary hover:border-border-secondary hover:bg-surface-hover'
              )}
            >
              {theme === id && (
                <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-accent-violet flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 text-white" />
                </div>
              )}
              <div
                className={clsx(
                  'w-10 h-10 rounded-lg flex items-center justify-center',
                  theme === id
                    ? 'bg-accent-violet text-white'
                    : 'bg-surface-active text-text-tertiary'
                )}
              >
                <Icon className="w-5 h-5" />
              </div>
              <div className="text-center">
                <p
                  className={clsx(
                    'text-[12px] font-medium',
                    theme === id ? 'text-accent-violet' : 'text-text-primary'
                  )}
                >
                  {label}
                </p>
                <p className="text-[10px] text-text-quaternary mt-0.5">{description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Color Palette */}
      <div>
        <h3 className="text-[13px] font-semibold text-text-primary mb-1">Palette colori</h3>
        <p className="text-[11px] text-text-tertiary mb-4">
          Scegli il colore principale dell'interfaccia
        </p>

        <div className="grid grid-cols-7 gap-2">
          {THEME_PALETTES.map(({ id, name, color }) => (
            <button
              key={id}
              onClick={() => onPaletteChange(id)}
              title={name}
              className={clsx(
                'relative aspect-square rounded-xl transition-all flex items-center justify-center',
                palette === id
                  ? 'ring-2 ring-offset-2 ring-offset-surface-secondary'
                  : 'hover:scale-110'
              )}
              style={{
                backgroundColor: color,
                boxShadow: palette === id ? `0 0 20px ${color}40` : undefined,
              }}
            >
              {palette === id && <Check className="w-4 h-4 text-white drop-shadow-md" />}
            </button>
          ))}
        </div>

        {/* Preview */}
        <div className="mt-6 p-4 rounded-xl bg-surface-tertiary/50 border border-border-primary">
          <p className="text-[11px] text-text-quaternary uppercase tracking-wider font-medium mb-3">
            Anteprima
          </p>
          <div className="flex items-center gap-3">
            <button
              className="px-4 py-2 rounded-lg text-[12px] font-medium text-white transition-all"
              style={{ backgroundColor: THEME_PALETTES.find((p) => p.id === palette)?.color }}
            >
              Pulsante primario
            </button>
            <button
              className="px-4 py-2 rounded-lg text-[12px] font-medium transition-all"
              style={{
                backgroundColor: `${THEME_PALETTES.find((p) => p.id === palette)?.color}20`,
                color: THEME_PALETTES.find((p) => p.id === palette)?.color,
              }}
            >
              Pulsante secondario
            </button>
            <span
              className="px-2.5 py-1 rounded-full text-[10px] font-semibold"
              style={{
                backgroundColor: `${THEME_PALETTES.find((p) => p.id === palette)?.color}20`,
                color: THEME_PALETTES.find((p) => p.id === palette)?.color,
              }}
            >
              Badge
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserSettings() {
  const { user } = useAuth();
  const fullName = user?.user_metadata?.full_name || 'Utente';
  const email = user?.email || '';
  const avatarUrl = user?.user_metadata?.avatar_url;
  const initials = fullName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="space-y-8">
      {/* Profile Info */}
      <div>
        <h3 className="text-[13px] font-semibold text-text-primary mb-1">Profilo utente</h3>
        <p className="text-[11px] text-text-tertiary mb-4">
          Gestisci le tue informazioni personali
        </p>

        <div className="flex items-start gap-4 mb-4">
          <div className="relative">
            {avatarUrl ? (
              <img src={avatarUrl} alt={fullName} className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-accent-emerald to-accent-blue flex items-center justify-center">
                <span className="text-lg font-bold text-white">{initials}</span>
              </div>
            )}
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <label className="block text-[11px] font-medium text-text-secondary mb-1.5">
                Nome completo
              </label>
              <input
                type="text"
                defaultValue={fullName}
                disabled
                className="w-full px-3 py-2 rounded-lg bg-surface-tertiary/50 border border-border-primary text-[12px] text-text-quaternary cursor-not-allowed transition-all"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-text-secondary mb-1.5">
                Email
              </label>
              <input
                type="email"
                defaultValue={email}
                disabled
                className="w-full px-3 py-2 rounded-lg bg-surface-tertiary/50 border border-border-primary text-[12px] text-text-quaternary cursor-not-allowed transition-all"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const SYNC_DIRECTION_OPTIONS: { value: 'bidirectional' | 'import_only' | 'export_only'; label: string; description: string }[] = [
  { value: 'import_only', label: 'Solo importazione', description: 'Gli eventi di Google Calendar vengono importati in Zenda' },
  { value: 'export_only', label: 'Solo esportazione', description: 'I task di Zenda vengono esportati su Google Calendar' },
  { value: 'bidirectional', label: 'Bidirezionale', description: 'Sincronizzazione completa in entrambe le direzioni' },
];

function GoogleCalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18.316 5.684H5.684v12.632h12.632V5.684z" fill="#fff" />
      <path d="M18.316 23.368l4.421-4.421h-4.421v4.421z" fill="#EA4335" />
      <path d="M23.368 5.684h-5.052v12.632h5.052V5.684z" fill="#FBBC04" />
      <path d="M18.316 18.316H5.684v5.052h12.632v-5.052z" fill="#34A853" />
      <path d="M0 18.316v3.789A1.263 1.263 0 001.263 23.368h3.79v-5.052H0z" fill="#188038" />
      <path d="M23.368 5.684V1.895A1.263 1.263 0 0022.105.632h-3.79v5.052h5.053z" fill="#1967D2" />
      <path d="M18.316.632H1.895A1.263 1.263 0 00.632 1.895v16.421h5.052V5.684h12.632V.632z" fill="#4285F4" />
      <path d="M8.401 16.264a3.09 3.09 0 01-1.227-.914l.687-.534c.219.32.485.564.796.734.312.17.654.254 1.026.254.384 0 .718-.098 1.002-.295.284-.196.426-.464.426-.802 0-.35-.152-.622-.457-.816-.305-.194-.688-.291-1.15-.291h-.714v-.748h.642c.397 0 .729-.084.996-.253.267-.168.401-.414.401-.738 0-.294-.12-.528-.36-.702-.24-.174-.54-.261-.9-.261-.35 0-.633.074-.849.222a2.173 2.173 0 00-.564.58l-.675-.546c.197-.314.474-.572.834-.774.36-.202.784-.303 1.272-.303.378 0 .714.06 1.008.18s.524.29.69.51c.166.22.249.474.249.762 0 .326-.094.598-.282.816-.188.218-.43.378-.726.48v.036c.344.102.622.279.834.531.212.252.318.555.318.909 0 .326-.09.618-.27.876-.18.258-.434.462-.762.612-.328.15-.71.225-1.146.225zm6.206 0c-.643 0-1.17-.227-1.583-.681-.413-.454-.62-1.055-.62-1.803v-.369c0-.498.09-.942.273-1.332.182-.39.435-.694.76-.912.324-.218.687-.327 1.089-.327.599 0 1.071.207 1.416.621.345.414.518.996.518 1.746v.456h-3.312v.072c.012.504.15.916.414 1.236.264.32.606.48 1.026.48.304 0 .565-.054.783-.162.218-.108.414-.27.588-.486l.48.468c-.432.594-1.046.891-1.842.891h.01zm-.081-4.671c-.348 0-.636.132-.864.396-.228.264-.369.633-.423 1.107h2.484v-.084c-.024-.45-.144-.8-.36-1.05-.216-.25-.496-.375-.84-.375l.003.006z" fill="#4285F4" />
    </svg>
  );
}

function IntegrationsSettings() {
  const {
    connections,
    isConnected,
    isSyncing,
    isLoading,
    calendars,
    error,
    connect,
    disconnect,
    syncNow,
    fetchCalendars,
    updateConnectionSettings,
  } = useGoogleCalendar();

  const activeConnection = connections.find((c) => c.status === 'active');

  // Load calendars when connected
  useEffect(() => {
    if (activeConnection && calendars.length === 0) {
      fetchCalendars(activeConnection.id);
    }
  }, [activeConnection, calendars.length, fetchCalendars]);

  return (
    <div className="space-y-8">
      {/* Google Calendar */}
      <div>
        <h3 className="text-[13px] font-semibold text-text-primary mb-1">Integrazioni</h3>
        <p className="text-[11px] text-text-tertiary mb-4">
          Collega servizi esterni per sincronizzare i tuoi dati
        </p>

        {/* Google Calendar Card */}
        <div className="bg-surface-secondary/30 rounded-xl border border-border-primary p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center border border-border-primary">
                <GoogleCalendarIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-text-primary">Google Calendar</p>
                <p className="text-[11px] text-text-tertiary">
                  Sincronizza i tuoi eventi con Zenda
                </p>
              </div>
            </div>

            {/* Status Badge */}
            {isConnected ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-accent-emerald/10 text-accent-emerald">
                <CheckCircle2 className="w-3 h-3" />
                Connesso
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-surface-tertiary text-text-quaternary">
                Non connesso
              </span>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-2.5 mb-3 rounded-lg bg-accent-rose/10 border border-accent-rose/20">
              <AlertCircle className="w-3.5 h-3.5 text-accent-rose flex-shrink-0" />
              <p className="text-[11px] text-accent-rose">{error}</p>
            </div>
          )}

          {isConnected && activeConnection ? (
            <div className="space-y-4">
              {/* Connected Account */}
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-surface-tertiary/50">
                <Calendar className="w-3.5 h-3.5 text-text-quaternary" />
                <span className="text-[11px] text-text-secondary">{activeConnection.googleEmail}</span>
              </div>

              {/* Sync Direction */}
              <div>
                <label className="block text-[11px] font-medium text-text-secondary mb-1.5">
                  Direzione sincronizzazione
                </label>
                <div className="space-y-1.5">
                  {SYNC_DIRECTION_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() =>
                        updateConnectionSettings(activeConnection.id, {
                          syncDirection: option.value,
                        })
                      }
                      className={clsx(
                        'w-full flex items-start gap-2.5 p-2.5 rounded-lg border text-left transition-all',
                        activeConnection.syncDirection === option.value
                          ? 'bg-accent-violet/5 border-accent-violet/30'
                          : 'bg-surface-tertiary/30 border-border-primary hover:border-border-secondary'
                      )}
                    >
                      <div
                        className={clsx(
                          'w-3.5 h-3.5 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                          activeConnection.syncDirection === option.value
                            ? 'border-accent-violet'
                            : 'border-border-secondary'
                        )}
                      >
                        {activeConnection.syncDirection === option.value && (
                          <div className="w-1.5 h-1.5 rounded-full bg-accent-violet" />
                        )}
                      </div>
                      <div>
                        <p
                          className={clsx(
                            'text-[12px] font-medium',
                            activeConnection.syncDirection === option.value
                              ? 'text-accent-violet'
                              : 'text-text-primary'
                          )}
                        >
                          {option.label}
                        </p>
                        <p className="text-[10px] text-text-quaternary">{option.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Calendar Selection */}
              {calendars.length > 0 && (
                <div>
                  <label className="block text-[11px] font-medium text-text-secondary mb-1.5">
                    Calendari da sincronizzare
                  </label>
                  <div className="space-y-1">
                    {calendars.map((cal) => {
                      const isSelected = activeConnection.selectedCalendarIds.includes(cal.id);
                      return (
                        <button
                          key={cal.id}
                          onClick={() => {
                            const newIds = isSelected
                              ? activeConnection.selectedCalendarIds.filter((id) => id !== cal.id)
                              : [...activeConnection.selectedCalendarIds, cal.id];
                            updateConnectionSettings(activeConnection.id, {
                              selectedCalendarIds: newIds,
                            });
                          }}
                          className={clsx(
                            'w-full flex items-center gap-2.5 p-2 rounded-lg border text-left transition-all',
                            isSelected
                              ? 'bg-accent-violet/5 border-accent-violet/30'
                              : 'bg-surface-tertiary/30 border-border-primary hover:border-border-secondary'
                          )}
                        >
                          <div
                            className={clsx(
                              'w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border',
                              isSelected
                                ? 'bg-accent-violet border-accent-violet'
                                : 'border-border-secondary'
                            )}
                          >
                            {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <div className="flex items-center gap-2 min-w-0">
                            {cal.backgroundColor && (
                              <div
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: cal.backgroundColor }}
                              />
                            )}
                            <span className="text-[12px] text-text-primary truncate">
                              {cal.summary}
                            </span>
                            {cal.primary && (
                              <span className="text-[9px] text-text-quaternary bg-surface-tertiary px-1.5 py-0.5 rounded flex-shrink-0">
                                principale
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Last Sync + Actions */}
              <div className="flex items-center justify-between pt-2 border-t border-border-primary">
                <div className="text-[10px] text-text-quaternary">
                  {activeConnection.lastFullSyncAt ? (
                    <>
                      Ultima sincronizzazione:{' '}
                      {new Date(activeConnection.lastFullSyncAt).toLocaleString('it-IT', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </>
                  ) : (
                    'Mai sincronizzato'
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={syncNow}
                    disabled={isSyncing}
                    className={clsx(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all',
                      isSyncing
                        ? 'bg-surface-tertiary text-text-quaternary cursor-not-allowed'
                        : 'bg-accent-violet/10 text-accent-violet hover:bg-accent-violet/20'
                    )}
                  >
                    {isSyncing ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3 h-3" />
                    )}
                    {isSyncing ? 'Sincronizzazione...' : 'Sincronizza ora'}
                  </button>
                  <button
                    onClick={() => disconnect(activeConnection.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-accent-rose/10 text-accent-rose hover:bg-accent-rose/20 transition-all"
                  >
                    Disconnetti
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-[11px] text-text-tertiary leading-relaxed">
                Collega il tuo account Google Calendar per visualizzare i tuoi eventi direttamente in
                Zenda e sincronizzare i task pianificati con il tuo calendario.
              </p>
              <div className="flex items-start gap-2 text-[11px] text-text-quaternary">
                <ExternalLink className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  Zenda richiede l'accesso in lettura e scrittura al tuo calendario per la
                  sincronizzazione degli eventi.
                </span>
              </div>
              <button
                onClick={connect}
                disabled={isLoading}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-accent-violet text-white text-[12px] font-medium hover:bg-accent-violet/90 transition-all"
              >
                <GoogleCalendarIcon className="w-4 h-4" />
                Connetti Google Calendar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* MCP — Authorized AI clients */}
      <McpClientsCard />
    </div>
  );
}

// ----- MCP authorized clients -----

interface McpAuthorizationRow {
  id: string;
  client_id: string;
  scope: string;
  authorized_at: string;
  last_used_at: string | null;
  client_name: string;
}

const MCP_URL = 'https://www.zenda.space/mcp';

function formatMcpDate(iso: string | null): string {
  if (!iso) return 'mai';
  try {
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function mcpScopeBadge(scope: string): { label: string; color: string } {
  if (scope === 'mcp') return { label: 'accesso completo', color: 'bg-accent-violet/15 text-accent-violet' };
  if (scope.includes('write')) return { label: 'lettura + scrittura', color: 'bg-accent-amber/15 text-accent-amber' };
  if (scope.includes('read')) return { label: 'sola lettura', color: 'bg-accent-emerald/15 text-accent-emerald' };
  return { label: scope, color: 'bg-surface-tertiary text-text-tertiary' };
}

function McpClientsCard() {
  const [rows, setRows] = useState<McpAuthorizationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: queryError } = await supabase
      .from('mcp_user_authorizations')
      .select('id, client_id, scope, authorized_at, last_used_at, revoked_at')
      .is('revoked_at', null)
      .order('authorized_at', { ascending: false });
    if (queryError) {
      setError(queryError.message);
      setLoading(false);
      return;
    }
    const enriched = await Promise.all(
      (data || []).map(async (row) => {
        try {
          const res = await fetch(`/oauth/clients/${encodeURIComponent(row.client_id)}`);
          const body = res.ok ? ((await res.json()) as { client_name?: string }) : null;
          return { ...row, client_name: body?.client_name || row.client_id };
        } catch {
          return { ...row, client_name: row.client_id };
        }
      }),
    );
    setRows(enriched);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  async function handleRevoke(id: string, clientName: string) {
    if (!confirm(`Revocare l'accesso a "${clientName}"? Tutti i token attivi saranno invalidati.`)) return;
    setRevoking(id);
    const { error: deleteError } = await supabase.from('mcp_user_authorizations').delete().eq('id', id);
    setRevoking(null);
    if (deleteError) {
      setError(deleteError.message);
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-1">
        <h3 className="text-[13px] font-semibold text-text-primary">Client MCP autorizzati</h3>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1 text-[10px] text-text-quaternary hover:text-text-tertiary disabled:opacity-50"
          title="Ricarica"
        >
          <RefreshCw className={clsx('w-3 h-3', loading && 'animate-spin')} />
        </button>
      </div>
      <p className="text-[11px] text-text-tertiary mb-4">
        Applicazioni AI (Claude Desktop, Claude Code, ecc.) con accesso ai tuoi dati Zenda via Model
        Context Protocol. Configura{' '}
        <code className="px-1 py-0.5 rounded bg-surface-tertiary text-accent-violet font-mono text-[10px]">
          {MCP_URL}
        </code>{' '}
        come server URL nel client per autorizzarne uno nuovo.
      </p>

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-accent-rose/10 border border-accent-rose/20 mb-3">
          <AlertCircle className="w-3.5 h-3.5 text-accent-rose shrink-0" />
          <p className="text-[11px] text-accent-rose">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-4 h-4 text-accent-violet animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-6 bg-surface-secondary/30 rounded-xl border border-border-primary border-dashed">
          <ShieldCheck className="w-6 h-6 text-text-quaternary mx-auto mb-2" />
          <p className="text-[11px] text-text-tertiary">Nessun client MCP autorizzato</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => {
            const badge = mcpScopeBadge(row.scope);
            return (
              <div
                key={row.id}
                className="flex items-center gap-3 p-3 rounded-xl bg-surface-secondary/30 border border-border-primary"
              >
                <div className="w-10 h-10 rounded-lg bg-accent-violet/10 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5 text-accent-violet" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-[13px] font-semibold text-text-primary truncate">{row.client_name}</p>
                    <span className={clsx('text-[9px] px-1.5 py-0.5 rounded-full font-medium', badge.color)}>
                      {badge.label}
                    </span>
                  </div>
                  <p className="text-[10px] text-text-quaternary">
                    Autorizzato {formatMcpDate(row.authorized_at)} · ultimo uso {formatMcpDate(row.last_used_at)}
                  </p>
                </div>
                <button
                  onClick={() => handleRevoke(row.id, row.client_name)}
                  disabled={revoking === row.id}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-accent-rose/10 text-accent-rose hover:bg-accent-rose/20 transition-all disabled:opacity-50"
                  title="Revoca accesso"
                >
                  {revoking === row.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3" />
                  )}
                  Revoca
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const DIGEST_OPTIONS: { value: 'daily' | 'weekly' | 'none'; label: string; description: string }[] = [
  { value: 'daily', label: 'Giornaliero', description: 'Ricevi un riepilogo ogni mattina con i task in scadenza e completati' },
  { value: 'weekly', label: 'Settimanale', description: 'Ricevi un riepilogo ogni lunedì con il bilancio della settimana' },
  { value: 'none', label: 'Disattivato', description: 'Non ricevere email di digest' },
];

function NotificationsSettings() {
  const { user } = useAuth();
  const { currentOrg } = useOrganization();
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'none'>('daily');
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentOrg?.id) return;
    let cancelled = false;
    setLoading(true);
    getDigestPreference(currentOrg.id)
      .then(pref => {
        if (cancelled) return;
        if (pref) {
          setFrequency(pref.frequency);
          setEnabled(pref.enabled);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentOrg?.id]);

  const handleSave = async (newFrequency: 'daily' | 'weekly' | 'none', newEnabled: boolean) => {
    if (!user?.id || !currentOrg?.id) return;
    setSaving(true);
    try {
      await upsertDigestPreference({
        userId: user.id,
        orgId: currentOrg.id,
        frequency: newFrequency,
        enabled: newEnabled,
      });
      setFrequency(newFrequency);
      setEnabled(newEnabled);
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-text-quaternary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-[13px] font-semibold text-text-primary mb-1">Email Digest</h3>
        <p className="text-[11px] text-text-tertiary mb-4">
          Ricevi un riepilogo dei tuoi task via email
        </p>

        {/* Enable toggle */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-surface-tertiary/50 border border-border-primary mb-4">
          <div>
            <p className="text-[12px] font-medium text-text-primary">Abilita digest email</p>
            <p className="text-[10px] text-text-quaternary mt-0.5">
              {currentOrg?.name || 'Organizzazione corrente'}
            </p>
          </div>
          <button
            onClick={() => handleSave(frequency, !enabled)}
            disabled={saving}
            className={clsx(
              'relative w-9 h-5 rounded-full transition-colors',
              enabled ? 'bg-accent-violet' : 'bg-surface-active'
            )}
          >
            <div className={clsx(
              'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
              enabled ? 'translate-x-4' : 'translate-x-0.5'
            )} />
          </button>
        </div>

        {/* Frequency */}
        {enabled && (
          <div>
            <label className="block text-[11px] font-medium text-text-secondary mb-1.5">
              Frequenza
            </label>
            <div className="space-y-1.5">
              {DIGEST_OPTIONS.map(option => (
                <button
                  key={option.value}
                  onClick={() => handleSave(option.value, option.value !== 'none')}
                  disabled={saving}
                  className={clsx(
                    'w-full flex items-start gap-2.5 p-2.5 rounded-lg border text-left transition-all',
                    frequency === option.value
                      ? 'bg-accent-violet/5 border-accent-violet/30'
                      : 'bg-surface-tertiary/30 border-border-primary hover:border-border-secondary'
                  )}
                >
                  <div className={clsx(
                    'w-3.5 h-3.5 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                    frequency === option.value ? 'border-accent-violet' : 'border-border-secondary'
                  )}>
                    {frequency === option.value && (
                      <div className="w-1.5 h-1.5 rounded-full bg-accent-violet" />
                    )}
                  </div>
                  <div>
                    <p className={clsx(
                      'text-[12px] font-medium',
                      frequency === option.value ? 'text-accent-violet' : 'text-text-primary'
                    )}>
                      {option.label}
                    </p>
                    <p className="text-[10px] text-text-quaternary">{option.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
